import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {
    createSubAccount,
    createSubAccountErrorSelector,
    createSubAccountStatusSelector,
    getSubAccounts,
    changeView,
    resetCreateSubAccountState,
    setPendingSelectUsername,
} from '../../features/subaccounts/subaccountSlice';

const initialValues = {
    name: '',
    company: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
};

const validateFields = (values) => {
    const errors = {};
    const trimmedName = values.name.trim();
    const trimmedCompany = values.company.trim();
    const trimmedUsername = values.username.trim();

    if (trimmedName.length < 5) {
        errors.name = 'Enter full name with surname.';
    }
    if (trimmedCompany.length < 5) {
        errors.company = 'Enter company name.';
    }
    if (!/^\d{10}$/.test(values.phone)) {
        errors.phone = 'Enter a valid 10 digit phone number.';
    }
    if (!trimmedUsername) {
        errors.username = 'Username is required.';
    }
    if (values.password.length < 6) {
        errors.password = 'Password must be at least 6 characters.';
    }
    if (!values.confirmPassword || values.confirmPassword !== values.password) {
        errors.confirmPassword = 'Passwords must match.';
    }

    return errors;
};

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: 16,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
        overflow: 'hidden',
    },
    dialogTitle: {
        padding: '24px 32px 16px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    titleText: {
        fontWeight: 700,
        letterSpacing: '0.02em',
    },
    subtitle: {
        marginTop: 6,
        color: 'rgba(226,232,240,0.85)',
    },
    divider: {
        borderColor: 'rgba(148,163,184,0.3)',
    },
    content: {
        padding: '24px 32px 32px',
    },
    field: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 12,
            boxShadow: '0 1px 0 rgba(15, 23, 42, 0.05)',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#38bdf8',
            borderWidth: 2,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
        },
    },
    statusArea: {
        marginTop: 20,
    },
    successList: {
        marginTop: 16,
        borderRadius: 16,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    },
    successListItem: {
        paddingLeft: 20,
        paddingRight: 20,
    },
    actions: {
        padding: '16px 32px 24px',
        backgroundColor: 'rgba(248, 250, 252, 0.9)',
    },
    primaryButton: {
        background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)',
        color: '#0f172a',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 12px 20px rgba(14, 165, 233, 0.25)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
        },
    },
    ghostButton: {
        textTransform: 'none',
    },
    chip: {
        fontWeight: 600,
    },
}));

const SubAccountCreate = ({ subAccountFormOpen, handleSubAccountFormClose }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const createStatus = useSelector(createSubAccountStatusSelector);
    const createError = useSelector(createSubAccountErrorSelector);

    const [formValues, setFormValues] = useState(initialValues);
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [submittedDetails, setSubmittedDetails] = useState(null);

    const fieldErrors = useMemo(() => validateFields(formValues), [formValues]);
    const isSubmitting = createStatus === 'pending';
    const isSuccess = createStatus === 'success';
    const isError = createStatus === 'error';
    const isFormValid = Object.keys(fieldErrors).length === 0;

    const showFieldError = (field) => {
        if (!fieldErrors[field]) {
            return '';
        }
        return touched[field] || submitAttempted ? fieldErrors[field] : '';
    };

    const handleChange = (field) => (event) => {
        setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
        if (createStatus !== 'idle') {
            dispatch(resetCreateSubAccountState());
        }
    };

    const handleBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const resetForm = () => {
        setFormValues(initialValues);
        setTouched({});
        setSubmitAttempted(false);
        setSubmittedDetails(null);
        dispatch(resetCreateSubAccountState());
    };

    const closeForm = () => {
        resetForm();
        handleSubAccountFormClose();
    };

    const handleSelectVehicles = () => {
        const username = submittedDetails?.username;
        if (username) {
            dispatch(setPendingSelectUsername(username));
        }
        dispatch(getSubAccounts());
        dispatch(changeView('vehicleSelect'));
        closeForm();
    };

    const submitForm = () => {
        setSubmitAttempted(true);
        if (!isFormValid) {
            return;
        }
        const details = {
            name: formValues.name.trim(),
            company: formValues.company.trim(),
            phone: formValues.phone,
            username: formValues.username.trim(),
        };
        setSubmittedDetails(details);
        dispatch(
            createSubAccount({
                name: details.name,
                company: details.company,
                phone: details.phone,
                username: details.username,
                password: formValues.password,
            })
        );
    };

    const statusConfig = {
        idle: { label: 'Ready' },
        pending: { label: 'Creating' },
        success: { label: 'Created' },
        error: { label: 'Action Needed' },
    };
    const status = statusConfig[createStatus] || statusConfig.idle;
    const statusChipStyles = {
        idle: {
            backgroundColor: 'rgba(56, 189, 248, 0.2)',
            color: '#e0f2fe',
            border: '1px solid rgba(56, 189, 248, 0.65)',
        },
        pending: {
            backgroundColor: 'rgba(14, 165, 233, 0.25)',
            color: '#e0f2fe',
            border: '1px solid rgba(56, 189, 248, 0.75)',
        },
        success: {
            backgroundColor: 'rgba(34, 197, 94, 0.22)',
            color: '#dcfce7',
            border: '1px solid rgba(34, 197, 94, 0.7)',
        },
        error: {
            backgroundColor: 'rgba(248, 113, 113, 0.22)',
            color: '#fee2e2',
            border: '1px solid rgba(248, 113, 113, 0.8)',
        },
    };
    const chipStyle = statusChipStyles[createStatus] || statusChipStyles.idle;

    return (
        <Dialog
            open={subAccountFormOpen}
            onClose={closeForm}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Box className={classes.titleRow}>
                    <Box>
                        <Typography variant="h5" className={classes.titleText}>
                            Create Sub Account
                        </Typography>
                        <Typography variant="body2" className={classes.subtitle}>
                            Add user to your account with limited access and a secure login.
                        </Typography>
                    </Box>
                    <Chip
                        label={status.label}
                        size="small"
                        className={classes.chip}
                        style={chipStyle}
                        icon={
                            isSubmitting ? (
                                <CircularProgress size={14} style={{ color: '#e2e8f0' }} />
                            ) : undefined
                        }
                    />
                </Box>
            </DialogTitle>
            <Divider className={classes.divider} />
            <DialogContent className={classes.content}>
                {!isSuccess && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Full Name"
                                name="name"
                                value={formValues.name}
                                onChange={handleChange('name')}
                                onBlur={handleBlur('name')}
                                error={Boolean(showFieldError('name'))}
                                helperText={showFieldError('name') || ' '}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Company"
                                name="company"
                                value={formValues.company}
                                onChange={handleChange('company')}
                                onBlur={handleBlur('company')}
                                error={Boolean(showFieldError('company'))}
                                helperText={showFieldError('company') || ' '}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Phone Number"
                                name="phone"
                                value={formValues.phone}
                                onChange={handleChange('phone')}
                                onBlur={handleBlur('phone')}
                                error={Boolean(showFieldError('phone'))}
                                helperText={showFieldError('phone') || ' '}
                                disabled={isSubmitting}
                                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Username"
                                name="username"
                                value={formValues.username}
                                onChange={handleChange('username')}
                                onBlur={handleBlur('username')}
                                error={Boolean(showFieldError('username'))}
                                helperText={showFieldError('username') || ' '}
                                disabled={isSubmitting}
                                autoComplete="off"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Password"
                                name="password"
                                type="password"
                                value={formValues.password}
                                onChange={handleChange('password')}
                                onBlur={handleBlur('password')}
                                error={Boolean(showFieldError('password'))}
                                helperText={showFieldError('password') || ' '}
                                disabled={isSubmitting}
                                autoComplete="new-password"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                className={classes.field}
                                variant="outlined"
                                fullWidth
                                label="Confirm Password"
                                name="confirmPassword"
                                type="password"
                                value={formValues.confirmPassword}
                                onChange={handleChange('confirmPassword')}
                                onBlur={handleBlur('confirmPassword')}
                                error={Boolean(showFieldError('confirmPassword'))}
                                helperText={showFieldError('confirmPassword') || ' '}
                                disabled={isSubmitting}
                                autoComplete="new-password"
                            />
                        </Grid>
                    </Grid>
                )}
                <Box className={classes.statusArea}>
                    {isSubmitting && (
                        <Alert
                            severity="info"
                            icon={<CircularProgress size={18} color="inherit" />}
                        >
                            Creating sub account. Please wait...
                        </Alert>
                    )}
                    {isSuccess && (
                        <Alert severity="success">
                            Sub account created successfully.
                        </Alert>
                    )}
                    {isError && (
                        <Alert severity="error">
                            {createError || 'Unable to create sub account.'}
                        </Alert>
                    )}
                </Box>
                {isSuccess && (
                    <List className={classes.successList}>
                        <ListItem className={classes.successListItem}>
                            <ListItemText
                                primary="Name"
                                secondary={submittedDetails?.name || '—'}
                            />
                        </ListItem>
                        <Divider className={classes.divider} />
                        <ListItem className={classes.successListItem}>
                            <ListItemText
                                primary="Company"
                                secondary={submittedDetails?.company || '—'}
                            />
                        </ListItem>
                        <Divider className={classes.divider} />
                        <ListItem className={classes.successListItem}>
                            <ListItemText
                                primary="Phone"
                                secondary={submittedDetails?.phone || '—'}
                            />
                        </ListItem>
                        <Divider className={classes.divider} />
                        <ListItem className={classes.successListItem}>
                            <ListItemText
                                primary="Username"
                                secondary={submittedDetails?.username || '—'}
                            />
                        </ListItem>
                    </List>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                {isSuccess ? (
                    <>
                        <Button
                            onClick={handleSelectVehicles}
                            variant="outlined"
                            className={classes.ghostButton}
                        >
                            Select Vehicles
                        </Button>
                        <Button
                            onClick={closeForm}
                            color="inherit"
                            variant="text"
                            className={classes.ghostButton}
                        >
                            Close
                        </Button>
                        <Button
                            onClick={resetForm}
                            variant="contained"
                            className={classes.primaryButton}
                        >
                            Create Another Sub Account
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={closeForm}
                            color="inherit"
                            variant="text"
                            className={classes.ghostButton}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={resetForm}
                            variant="outlined"
                            className={classes.ghostButton}
                            disabled={isSubmitting}
                        >
                            Reset Form
                        </Button>
                        <Button
                            onClick={submitForm}
                            variant="contained"
                            disabled={!isFormValid || isSubmitting}
                            className={classes.primaryButton}
                        >
                            Create Sub Account
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SubAccountCreate;
